import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import useGetCategories from "../hooks/useGetCategories";
import useGetUserInfo from "../hooks/useGetUserInfo";
import useGetProducts from "../hooks/useGetProducts";
import usePostProduct from "../../../../../Hooks/usePostProduct";
import useGetProductStatus from "../../../../../Hooks/useGetProductStatus";
import LoadingIcon from "../../../../../Assets/Icons/circular-determinate.svg";

import usePostProductRegenerate from "../hooks/usePostProductRegenerate";
import { useAIToolsContext } from "./AIToolsProvider";
import usePostProductAITools from "../hooks/usePostProductAITools";
import {
  AITools,
  FluxModel,
  WeddinglyModel,
  CreditCostMap,
  IdeogramModel,
  MB,
  NanoBananaModel,
  normalizeApiModelName,
  modelSupportsAspectRatio,
} from "../constant";
import { base64ToBlob } from "../utils";
import { useNavigate, useParams } from "react-router-dom";
import FailModal from "../../../../../Components/FailModal";
import useGetProjectDetail from "../hooks/useGetProjectDetail";
import OutOfCreditsModal from "../../../../../Components/OutOfCreditsModal";

export const formInitialState = {
  model: FluxModel.Dev,
  categoryId: 0,
  originalPrompt: "",
  prompt: "",
  negativePrompt: "",
  keepStructure: false,
  styleType: "", // only for Ideogram
  colorPalette: "", // only for Ideogram
  imageInputFile: null, // for Flux Kontext
  selectedImageUrl: null, // for Flux Kontext\
  aspectRatio: null,
};

export const GenerationErrorType = {
  General: "general",
  InappropriatePrompt: "inappropriatePrompt",
  InappropriateImage: "inappropriateImage",
};

const ADSContext = createContext({});

export const useADSContext = () => useContext(ADSContext);

const ADSProvider = ({ children }) => {
  const {
    selectedAITools,
    maskImage,
    resetAITools,
    firstSelectedImage,
    setFirstSelectedImage,
    firstSelectedImageServerPath,
    setFirstSelectedImageServerPath,
    firstBoundingBoxSelectedIndexes,
    secondSelectedImageServerPath,
    secondBoundingBoxSelectedIndexes,
    selectedAvatarIndex,
    selectedAvatarStrengthIndex,
    setFirstBoundingBox,
    setFirstBoundingBoxSelectedIndexes,
  } = useAIToolsContext();

  const navigate = useNavigate();

  const { projectId } = useParams();

  // state
  const [productFilter, setProductFilter] = useState({
    page: 0,
    size: 20,
    favorite: false,
  });
  const [form, setForm] = useState(formInitialState);

  // used only to retain form state immediately after product selection.
  // This will be used to compare changes with the current form (re-generation feature)
  const [
    formStateImmediatelyAfterProductSelection,
    setFormStateImmediatelyAfterProductSelection,
  ] = useState(formInitialState);

  const [generalCategory, setGeneralCategory] = useState({});
  const [selectedProduct, setSelectedProduct] = useState({});
  const [selectProductOnFirstLoad, setSelectProductOnFirstLoad] =
    useState(false); // select the first product, if exists, on initial load
  const [globalLoading, setGlobalLoading] = useState(false);

  // inappropriate error handling
  const [generationError, setGenerationError] = useState(null);

  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    description: "",
  });
  const [outOfCreditsModal, setOutOfCreditsModal] = useState({
    show: false,
    variant: "",
  });

  // query
  const {
    data: projectDetail,
    loading: loadingProjectDetail,
    getData: getProjectDetail,
  } = useGetProjectDetail({ projectId });
  const { data: categories, loading: loadingCategories } = useGetCategories();
  const {
    data: userInfo,
    loading: loadingUserInfo,
    getData: getUserInfo,
  } = useGetUserInfo();
  const {
    data: products,
    totalData: totalProducts,
    loading: loadingProducts,
    getData: getProducts,
    removeData: deleteProduct,
  } = useGetProducts({
    ...productFilter,
    projectId,
  });

  // mutate
  const postProduct = usePostProduct();
  const postProductRegenerate = usePostProductRegenerate();
  const getProductStatus = useGetProductStatus();
  const postProductAITools = usePostProductAITools();

  const isExternalModel = useMemo(
    () =>
      [
        FluxModel.FluxKontext,
        FluxModel.Ultimate,
        FluxModel.Pro,
        FluxModel.Dev,
        IdeogramModel.Ideogram2,
        IdeogramModel.IdeogramTurbo,
        IdeogramModel.Ideogram3,
        WeddinglyModel.Swift,
        NanoBananaModel.NanoBanana2,
      ].includes(form.model),
    [form.model],
  );
  const isIdeogramModel = useMemo(
    () =>
      [
        IdeogramModel.Ideogram2,
        IdeogramModel.IdeogramTurbo,
        IdeogramModel.Ideogram3,
      ].includes(form.model),
    [form.model],
  );
  const isFluxModel = useMemo(
    () =>
      [
        FluxModel.FluxKontext,
        FluxModel.Ultimate,
        FluxModel.Pro,
        FluxModel.Dev,
      ].includes(form.model),
    [form.model],
  );
  const isNanoBananaModel = useMemo(
    () => [NanoBananaModel.NanoBanana2].includes(form.model),
    [form.model],
  );
  const supportsAspectRatio = useMemo(
    () => modelSupportsAspectRatio(form.model),
    [form.model],
  );

  const formHasChanged = useMemo(() => {
    const isModelChanged =
      form.model !== formStateImmediatelyAfterProductSelection.model;
    const isCategoryChanged =
      form.categoryId !== formStateImmediatelyAfterProductSelection.categoryId;
    const isPromptChanged =
      form.prompt !== formStateImmediatelyAfterProductSelection.prompt;
    const isNegativePromptChanged =
      form.negativePrompt !==
      formStateImmediatelyAfterProductSelection.negativePrompt;
    const isKeepStructureChanged =
      form.keepStructure !==
      formStateImmediatelyAfterProductSelection.keepStructure;
    // const isStyleTypeChanged =
    //   form.styleType !== formStateImmediatelyAfterProductSelection.styleType;
    // const isColorPaletteChanged =
    //   form.colorPalette !==
    //   formStateImmediatelyAfterProductSelection.colorPalette;
    const isSelectedImageUrlChanged =
      form.selectedImageUrl !==
      formStateImmediatelyAfterProductSelection.selectedImageUrl;
    const isAspectRatioChanged =
      form.aspectRatio !==
      formStateImmediatelyAfterProductSelection.aspectRatio;

    if (isExternalModel) {
      const baseChanges =
        isModelChanged ||
        isPromptChanged ||
        (supportsAspectRatio && isAspectRatioChanged) ||
        !!selectedAITools;

      if (form.model === FluxModel.FluxKontext) {
        return baseChanges || isSelectedImageUrlChanged;
      }

      return baseChanges;
    }

    return (
      isModelChanged ||
      isPromptChanged ||
      isCategoryChanged ||
      isNegativePromptChanged ||
      isKeepStructureChanged ||
      !!selectedAITools
    );
  }, [
    form.categoryId,
    // form.colorPalette,
    form.keepStructure,
    form.model,
    form.negativePrompt,
    form.prompt,
    form.selectedImageUrl,
    // form.styleType,
    form.aspectRatio,
    formStateImmediatelyAfterProductSelection.categoryId,
    // formStateImmediatelyAfterProductSelection.colorPalette,
    formStateImmediatelyAfterProductSelection.keepStructure,
    formStateImmediatelyAfterProductSelection.model,
    formStateImmediatelyAfterProductSelection.negativePrompt,
    formStateImmediatelyAfterProductSelection.prompt,
    formStateImmediatelyAfterProductSelection.selectedImageUrl,
    // formStateImmediatelyAfterProductSelection.styleType,
    formStateImmediatelyAfterProductSelection.aspectRatio,
    isExternalModel,
    supportsAspectRatio,
    selectedAITools,
  ]);

  const handleProductSelectionByAITools = useCallback(
    (product) => {
      if (
        [AITools.AvatarConverter, AITools.FaceCorrection].includes(
          selectedAITools,
        )
      ) {
        setFirstSelectedImageServerPath(
          product.imageEditedUrl || product.imageUrl || null,
        );
        setFirstBoundingBox([]);
        setFirstBoundingBoxSelectedIndexes(null);
      }
    },
    [
      selectedAITools,
      setFirstBoundingBox,
      setFirstBoundingBoxSelectedIndexes,
      setFirstSelectedImageServerPath,
    ],
  );

  const handleSelectProduct = useCallback(
    (product, aiTools = selectedAITools, persistSelection = false) => {
      setGenerationError(null);

      const isProductExist = !!product.id;
      const isSameProductSelected = selectedProduct.id === product.id;
      const isAIToolsSelected = !!aiTools;

      // Prepare the default form data
      const defaultFormData = {
        ...formInitialState,
        model: FluxModel.Dev,
        categoryId: generalCategory.id || 0,
      };

      const isHighlightAndSwap = [
        AITools.HighlightAndSwap,
        AITools.HighlightAndSwap2,
      ].includes(selectedAITools);

      // Case 1: New valid product with AI tools active
      if (isProductExist && !isSameProductSelected && isAIToolsSelected) {
        handleProductSelectionByAITools(product);

        const formData = {
          ...defaultFormData,
          prompt: isHighlightAndSwap ? form.prompt : product.prompt || "",
          originalPrompt: isHighlightAndSwap
            ? form.prompt
            : product.prompt || "",
          negativePrompt: product.negativePrompt || "",
          keepStructure: product.keepStructure || false,
        };

        setForm(formData);
        setFormStateImmediatelyAfterProductSelection(formData);
        setSelectedProduct(product);
        setFirstSelectedImage(null);
      }
      // Case 2: Invalid or same product with AI tools active
      else if (
        (!isProductExist || isSameProductSelected) &&
        isAIToolsSelected
      ) {
        handleProductSelectionByAITools({});

        setForm({
          ...defaultFormData,
          prompt: isHighlightAndSwap ? form.prompt : "",
          originalPrompt: isHighlightAndSwap ? form.prompt : "",
        });
        setFormStateImmediatelyAfterProductSelection(defaultFormData);
        setSelectedProduct({});
      }
      // Case 3: Valid product without AI tools (or forcing selection)
      else if (isProductExist && (!isSameProductSelected || persistSelection)) {
        resetAITools();

        // Handle model selection logic
        const modelName = normalizeApiModelName(
          product.modelName || FluxModel.Dev,
        );

        const formData = {
          ...defaultFormData,
          model: modelName,
          categoryId: product.category?.id || generalCategory.id || 0,
          prompt: product.prompt || "",
          originalPrompt: product.prompt || "",
          negativePrompt: product.negativePrompt || "",
          keepStructure: product.keepStructure || false,
          // styleType: product.styleType || "", // only for Ideogram
          // colorPalette: product.colorPalette || "", // only for Ideogram
          selectedImageUrl: product.imageUrl || null,
          aspectRatio: product.aspectRatio || null,
        };

        setForm(formData);
        setFormStateImmediatelyAfterProductSelection(formData);
        setSelectedProduct(product);
      }
      // Case 4: Default fallback - reset everything
      else {
        resetAITools();
        setForm({
          ...formInitialState,
          categoryId: generalCategory.id || 0,
        });
        setFormStateImmediatelyAfterProductSelection({
          ...formInitialState,
          categoryId: generalCategory.id || 0,
        });
        setSelectedProduct({});
      }
    },
    [
      form.prompt,
      generalCategory.id,
      handleProductSelectionByAITools,
      resetAITools,
      selectedAITools,
      selectedProduct.id,
      setFirstSelectedImage,
    ],
  );

  const handleProductCreationSuccess = useCallback(
    async ({ productId, withStatusCheck = false }) => {
      try {
        if (withStatusCheck && productId) {
          const res = await getProductStatus({ productId });
          console.log("productStatus: ", res);
          if (res?.includes("Inappropriate image has been generated")) {
            setGenerationError(GenerationErrorType.InappropriateImage);
            return;
          }
        }
        setProductFilter({
          favorite: false,
          page: 0,
          size: 20,
        });
        const res = await getProducts(
          { projectId, favorite: false, page: 0, size: 20 },
          true,
        );
        const firstProduct = res[0] || {};
        handleSelectProduct(firstProduct, null, true);
        getUserInfo();
      } catch (e) {
        console.error(e);
        setGenerationError(GenerationErrorType.General);
      }
    },
    [
      getProductStatus,
      getProducts,
      getUserInfo,
      handleSelectProduct,
      projectId,
    ],
  );

  const constructPostProductAIToolsParams = useCallback(() => {
    const tool = selectedAITools;
    const modelName = form.model;

    switch (tool) {
      case AITools.HighlightAndSwap:
        return {
          projectId,
          tool,
          modelName,
          prompt: form.prompt,
          negativePrompt: form.negativePrompt,
          categoryId: form.categoryId,
          keepStructure: form.keepStructure,
          ...(maskImage && {
            imageMaskFile: base64ToBlob(maskImage, "image/png"),
          }),
          ...(firstSelectedImage
            ? { imageInputFile: firstSelectedImage }
            : { selectedImageUrl: selectedProduct.imageUrl }),
        };

      // HighlightAndSwap2 is indicator to use the model WeddinglyModel.StudioV2Plus
      case AITools.HighlightAndSwap2:
        return {
          projectId,
          tool: AITools.HighlightAndSwap,
          modelName: WeddinglyModel.StudioV2Plus, // must be this value
          prompt: form.prompt,
          negativePrompt: form.negativePrompt,
          categoryId: form.categoryId,
          keepStructure: form.keepStructure,
          ...(maskImage && {
            imageMaskFile: base64ToBlob(maskImage, "image/png"),
          }),
          ...(firstSelectedImage
            ? { imageInputFile: firstSelectedImage }
            : { selectedImageUrl: selectedProduct.imageUrl }),
        };

      case AITools.HighlightAndRemove:
        return {
          projectId,
          tool,
          modelName,
          ...(maskImage && {
            imageMaskFile: base64ToBlob(maskImage, "image/png"),
          }),
          ...(firstSelectedImage
            ? { imageInputFile: firstSelectedImage }
            : { selectedImageUrl: selectedProduct.imageUrl }),
        };

      case AITools.RemoveBackground:
        return {
          projectId,
          tool,
          modelName,
          ...(firstSelectedImage
            ? { imageInputFile: firstSelectedImage }
            : { selectedImageUrl: selectedProduct.imageUrl }),
        };

      case AITools.BlurBackground:
        return {
          projectId,
          tool,
          modelName,
          ...(firstSelectedImage
            ? { imageInputFile: firstSelectedImage }
            : { selectedImageUrl: selectedProduct.imageUrl }),
        };

      case AITools.SwapFace:
        return {
          projectId,
          tool,
          modelName,
          imageInputUrl: firstSelectedImageServerPath,
          imageSwapUrl: secondSelectedImageServerPath,
          inputImageFaceIndexes: firstBoundingBoxSelectedIndexes?.second,
          swapImageFaceIndex: secondBoundingBoxSelectedIndexes?.second,
        };

      case AITools.FaceCorrection:
        return {
          projectId,
          tool,
          modelName,
          imageInputUrl: firstSelectedImageServerPath,
          inputImageFaceIndexes: firstBoundingBoxSelectedIndexes?.second,
        };

      case AITools.AvatarConverter:
        return {
          projectId,
          tool,
          modelName,
          avatarStyleIndex: selectedAvatarIndex,
          avatarConversionStrength: selectedAvatarStrengthIndex,
          ...(firstSelectedImageServerPath
            ? { selectedImageUrl: firstSelectedImageServerPath }
            : { imageInputFile: firstSelectedImage }),
        };

      default:
        return {
          projectId,
          form,
          isExternalModel,
          isIdeogramModel,
          supportsAspectRatio,
          selectedImageUrl: selectedProduct.imageUrl,
        };
    }
  }, [
    firstBoundingBoxSelectedIndexes?.second,
    firstSelectedImage,
    firstSelectedImageServerPath,
    form,
    isExternalModel,
    isIdeogramModel,
    supportsAspectRatio,
    maskImage,
    projectId,
    secondBoundingBoxSelectedIndexes?.second,
    secondSelectedImageServerPath,
    selectedAITools,
    selectedAvatarIndex,
    selectedAvatarStrengthIndex,
    selectedProduct,
  ]);

  const handleGenerationError = useCallback((err) => {
    const errMsg = err?.response?.data?.message || "";

    if (errMsg === "error.inappropriateprompt") {
      setGenerationError(GenerationErrorType.InappropriatePrompt);
    } else if (errMsg === "error.notenoughcredit") {
      setErrorModal({
        show: true,
        title: "Insufficient Credits",
        description:
          "This AI model requires more credits than you have. Subscribe for more or try a different model.",
        retryText: "Subscribe",
        cancelText: "Cancel",
        reverse: true,
      });
    } else if (errMsg === "error.http.413") {
      setErrorModal({
        show: true,
        retry: false,
        title: "Image Too Large",
        description:
          "The image you tried to upload is too large. The maximum file size allowed is 10 MB. Please upload a smaller image and try again. Thank You!",
        retryText: "",
      });
    } else {
      setGenerationError(GenerationErrorType.General);
    }
  }, []);

  const resetFailModal = () => {
    setErrorModal({
      show: false,
      title: "",
      description: "",
      retry: false,
      retryText: "",
    });
  };

  const closeOutOfCreditsModal = () => {
    setOutOfCreditsModal({
      show: false,
      variant: "",
    });
  };

  const handleGenerateProduct = useCallback(() => {
    const useCredit = CreditCostMap[form.model] || CreditCostMap.others;
    const { balance } = userInfo.userCredit || {};

    // if not enough credit, only for flux model, since in other parts we will disable generate button
    if (balance < useCredit) {
      setErrorModal({
        show: true,
        title: "Insufficient Credits",
        description:
          "This AI model requires more credits than you have. Subscribe for more or try a different model.",
        retryText: "Subscribe",
        cancelText: "Cancel",
        reverse: true,
      });
      return;
    }

    if (
      form.model === FluxModel.FluxKontext &&
      !form.imageInputFile &&
      !form.selectedImageUrl
    ) {
      setErrorModal({
        show: true,
        retry: false,
        title: "Image Required for AI Editing",
        description:
          "You need to upload an image before you can continue. Once your image is uploaded, enter your prompt and let WeddinglyAi work its magic to edit your image.",
        cancelText: "Ok",
        retryText: "",
      });
      return;
    }

    if (form.imageInputFile && form.imageInputFile.size > 10 * MB) {
      setErrorModal({
        show: true,
        retry: false,
        title: "Image Too Large",
        description:
          "The image you tried to upload is too large. The maximum file size allowed is 10 MB. Please upload a smaller image and try again. Thank You!",
        retryText: "",
      });
      return;
    }

    setGlobalLoading(true);
    setGenerationError(null);

    /**
     * Although they have the same endpoint
     * 1. postProduct focus on the form without AITools
     * 2. postProductAITools focus on AITools
     */
    const postProductApi = selectedAITools ? postProductAITools : postProduct;

    postProductApi(constructPostProductAIToolsParams())
      .then((res) =>
        handleProductCreationSuccess({
          productId: res.id,
          withStatusCheck: true,
        }),
      )
      .catch(handleGenerationError)
      .finally(() => setGlobalLoading(false));
  }, [
    form.model,
    form.imageInputFile,
    form.selectedImageUrl,
    userInfo.userCredit,
    selectedAITools,
    postProductAITools,
    postProduct,
    constructPostProductAIToolsParams,
    handleProductCreationSuccess,
    handleGenerationError,
  ]);

  const handleRetry = useCallback(() => {
    navigate("/Home/Profile?selectedTab=credit");
  }, [navigate]);

  const handleRegenerateProduct = useCallback(() => {
    setGlobalLoading(true);
    setGenerationError(null);
    postProductRegenerate({ productId: selectedProduct.id })
      .then((res) =>
        handleProductCreationSuccess({
          productId: res.id,
          withStatusCheck: true,
        }),
      )
      .catch(handleGenerationError)
      .finally(() => setGlobalLoading(false));
  }, [
    handleGenerationError,
    handleProductCreationSuccess,
    postProductRegenerate,
    selectedProduct.id,
  ]);

  // set general category information
  useEffect(() => {
    if (categories.length > 0 && !generalCategory.id) {
      const currentGeneralCategory = categories.find(
        (category) => category.modelName === "no_category",
      );
      if (currentGeneralCategory) {
        setGeneralCategory(currentGeneralCategory);
      }
    }
  }, [categories, generalCategory]);

  // select first product on first load only
  useEffect(() => {
    if (products.length && !selectProductOnFirstLoad && generalCategory.id) {
      const firstProduct = products[0];

      handleSelectProduct(firstProduct, null, true);
      setSelectProductOnFirstLoad(true);
    }
  }, [
    selectProductOnFirstLoad,
    products,
    generalCategory,
    handleSelectProduct,
  ]);

  // show not enough credits modal
  useEffect(() => {
    const { balance } = userInfo.userCredit || {};

    if (typeof balance === "number" && balance <= 0 && !loadingUserInfo) {
      const isModalShown = sessionStorage.getItem(
        "adsNotEnoughCreditsModalShown",
      );
      if (!isModalShown) {
        setOutOfCreditsModal({
          show: true,
          variant: "outOfCredits",
        });
      }
      sessionStorage.setItem("adsNotEnoughCreditsModalShown", "true");
    }
  }, [loadingUserInfo, userInfo.userCredit]);

  const contextValue = useMemo(() => {
    return {
      form,
      setForm,

      formHasChanged,

      projectId,

      projectDetail,
      getProjectDetail,
      loadingProjectDetail,

      categories,
      loadingCategories,

      userInfo,
      loadingUserInfo,

      products,
      totalProducts,
      deleteProduct,
      loadingProducts,

      productFilter,
      setProductFilter,

      selectedProduct,
      handleSelectProduct,

      handleGenerateProduct,
      handleRegenerateProduct,

      globalLoading,
      setGlobalLoading,

      isExternalModel,
      isIdeogramModel,
      isFluxModel,
      supportsAspectRatio,
      isNanoBananaModel,
      generalCategory,

      handleProductCreationSuccess,

      //error handling
      generationError,
      setGenerationError,

      errorModal,
      setErrorModal,

      outOfCreditsModal,
      setOutOfCreditsModal,
    };
  }, [
    form,
    formHasChanged,
    projectId,
    projectDetail,
    getProjectDetail,
    loadingProjectDetail,
    categories,
    loadingCategories,
    userInfo,
    loadingUserInfo,
    products,
    totalProducts,
    deleteProduct,
    loadingProducts,
    productFilter,
    selectedProduct,
    handleSelectProduct,
    handleGenerateProduct,
    handleRegenerateProduct,
    globalLoading,
    isExternalModel,
    isIdeogramModel,
    isNanoBananaModel,
    isFluxModel,
    supportsAspectRatio,
    generalCategory,
    handleProductCreationSuccess,
    generationError,
    errorModal,
    outOfCreditsModal,
  ]);

  return (
    <ADSContext.Provider value={contextValue}>
      {globalLoading && (
        <div className="overlay">
          <img alt="" className="loadingIcon" src={LoadingIcon} />
        </div>
      )}

      <FailModal
        key={2}
        {...errorModal}
        onRetry={handleRetry}
        onCancel={resetFailModal}
      />

      <OutOfCreditsModal
        {...outOfCreditsModal}
        onClose={closeOutOfCreditsModal}
        onSubscribe={handleRetry}
      />

      {children}
    </ADSContext.Provider>
  );
};

export default ADSProvider;
